import util from 'util';
import { isNumber, isString, first, last} from 'lodash';
import {
  TYPE_ARRAY, TYPE_OBJECT, TYPE_STRING, TYPE_VALUE, TYPE_TERM, TYPE_NULL,
} from './constants';

function has(object, key) {
  if (!(object && key)) throw new Error('has requires object and key');
  if (typeof object !== 'object') throw new Error('has(object) must be an object');
  if (typeof key !== 'string') throw new Error('has(_,key) must be a string');

  if (object instanceof Map) return object.has(key);
  const keys = Array.from(Object.keys(object));
  return keys.includes(key);
}

export default class Term {
  constructor(init = {}) {
    this.id = Term.nextId;
    Term.nextId += 1;
    this.copy(init);
  }

  clone(withParent) {
    const clone = new Term(this);
    if (withParent) {
      clone.parent = this.parent;
    }
    return clone;
  }

  copy({ props, value }) {
    if (props) {
      this.props = props;
    } else {
      this.props = new Map();
    }
    this.value = value;
  }

  get value() {
    if (!has(this, '_value')) return null;
    return this._value;
  }

  set value(value) {
    if (this === value) throw new Error('circular value');
    if (Array.isArray(value)) {
      if (value.includes(this)) {
        throw new Error('circular values');
      }
      value.forEach((subItem) => {
        if (subItem === this) throw new Error('circluar values');
        if (subItem && (Term.typeOf(subItem) === TYPE_TERM)) {
          // eslint-disable-next-line no-param-reassign
          subItem.parent = this;
        }
      });
    } else if (value instanceof Term) {
      // eslint-disable-next-line no-param-reassign
      value.parent = this;
    }
    this._value = value;
  }

  get isValidTime() {
    const { start, end } = this;

    return isNumber(start) && isNumber(end) && start <= end;
  }

  containsTime(t) {
    if (!this.isValidTime) return false;
    return t >= this.start && t < this.end;
  }

  intersects(start, end) {
    if (Array.isArray(start)) return this.intersects(...start);
    if (!(isNumber(start) && isNumber(end))) {
      return false;
    }
    return start < this.end && end > this.start;
  }

  intersectsRanges(ranges) {
    if (!ranges && Array.isArray(ranges)) {
      return false;
    }

    return ranges.reduce((isFound, range) => {
      if (isFound) return isFound;
      if (!Array.isArray(range)) return false;
      return this.intersects(...range);
    }, false);
  }

  filterRanges(ranges) {
    if (!ranges && Array.isArray(ranges)) {
      return [];
    }

    return ranges.filter((range) => this.intersects(range));
  }

  get parent() {
    return this._parent;
  }

  set parent(p) {
    if (p === this) throw new Error('circular structure');
    this._parent = p;
  }

  /**
   * returns the type of value the term contains
   */
  get type() {
    return Term.typeOf(this._value);
  }

  get isMeta() {
    return !!this.meta;
  }

  get meta() {
    return this.has('m') ? this._props.get('m') : null;
  }

  get word() {
    if (this.value && this.type === TYPE_STRING) return this.value;
    return (this.has('w')) ? this.get('w') : null;
  }

  set word(value) {
    this.set('w', value);
    this.value = value;
  }

  get p() {
    return this.has('p') ? this.get('p') : null;
  }

  get(name) {
    return this.has(name) ? this._props.get(name) : null;
  }

  /**
   * @TODO:  investgate/kill; looks sketchy ....
   *
   * @param name
   * @param topmost
   * @param found
   * @returns {Array|*}
   */
  getDeep(name, topmost = false, found = []) {
    if (this.has(name)) {
      found.push(this.get(name));
      if (!topmost && this.parent) {
        this.parent.getDeep(name, topmost, found);
      }
    } else if (this.parent) {
      return this.parent.getDeep(name, topmost, found);
    }

    return found;
  }

  set(name, value) {
    this.props.set(name, value);
  }

  getAll(name, pSet) {
    const set = pSet || new Set();
    if (this.has(name)) set.add(this.get(name));
    if (this.type === TYPE_ARRAY) {
      this.value.forEach((term) => {
        if (term instanceof Term) term.getAll(name, set);
      });
    }
    if (this.type === TYPE_TERM) {
      this.value.getAll(name, set);
    }
    return set;
  }

  has(name) {
    return this._props.has(name);
  }

  get start() {
    if ('_start' in this) return this._start;
    let start = null;

    this.getAll('s')
      .forEach((s) => {
        if ((start === null) || (isNumber(s) && start > s)) start = s;
      });
    return start;
  }

  freezeRange() {
    if ('_start' in this) return;
    this._start = this.start;
    this._end = this.end;
  }

  get end() {
    if ('_end' in this) return this._end;
    let end = null;

    this.getAll('e')
      .forEach((s) => {
        if ((end === null) || (isNumber(s) && end < s)) end = s;
      });
    return end;
  }

  get length() {
    return this.values.length;
  }

  /**
   * note- values ALWAYS returns value as an array.
   * @returns {*}
   */
  get values() {
    if (!('_value' in this)) {
      return [];
    }
    if (Array.isArray(this._value)) {
      return this._value.slice(0);
    }
    return [this._value];
  }

  set values(value) {
    if (value === this) {
      throw new Error('cannot include self as own value');
    }
    if (Array.isArray(value)) {
      if (value.includes(this)) {
        throw new Error('cannot include self in own value');
      }
    }
    this.value = value;
  }

  get props() {
    if (!this._props) this._props = new Map();
    return this._props;
  }

  set props(value) {
    if (!value) return;
    if (value instanceof Map) {
      this._props = new Map(value.entries());
    } else if (Array.isArray(value)) {
      this._props = new Map(value);
    } else if (typeof value === 'object') {
      this._props = new Map(Object.entries(value));
    } else {
      this._props = new Map();
    }
  }

  freezeWords() {
    this._words = this.getWords();
  }

  getWords(pList = []) {
    let list = pList;
    if (this._words) return pList.concat(this._words);

    if (this.get('type') === 'word') {
      list.push(this);
    }
    switch (this.type) {
      case TYPE_ARRAY:
        this.value.forEach((term) => {
          list = term.getWords(list);
        });
        break;
      case TYPE_TERM:
        list = this.value.getWords(list);
        break;
      default:
      // do nothing
    }
    return list;
  }

  getClasses(pContext) {
    const context = pContext || this.get('type') || 'word';
    let classes = [];
    if (!(this && this instanceof Term)) return classes;

    this.props.forEach((value, key) => {
      if (key && value && isString(key) && /^(range:|search)/.test(key)) {
        const className = key.trim()
          .replace(':', '-')
          .replace(/[\W]+/g, '-');
        classes.push(className);
        classes.push('range-true');
      }
    });

    if (this.has('p') && isNumber(this.get('p'))) {
      classes.push(`index-${this.get('p')}`);
    }

    if (context !== 'word') {
      const words = this.getWords();
      if (words.length) {
        const firstWord = first(words);
        const lastWord = last(words);
        if (firstWord.has('p')) classes.push(`p-start-${firstWord.get('p')}`);
        if (lastWord.has('p')) classes.push(`p-end-${lastWord.get('p')}`);
      }
    }

    classes = classes.map((c) => `${context}-${c}`)
      .join(' ');
    return classes;
  }
}

Term.nextId = 1;

function shortArray(a) {
  if (!Array.isArray(a)) throw new Error(`shortArray passed ${a}`);
  if (a.length < 5) return a;
  return [...a.slice(0, 2), '...', last(a)];
}

Term.propsSummary = (map) => {
  if (!(map instanceof Map)) {
    return '-- non map prop -- ';
  }
  return Array.from(map.entries())
    .map(([key, value]) => `${key} : ${Term.summaryOf(value)}`)
    .join(',\n');
};

Term.summaryOf = function summaryOf(series) {
  if (Array.isArray(series)) {
    return `[${shortArray(series)
      .map((item) => ((item instanceof Term) ? Term.summaryOf(item) : util.inspect(item)))}]`;
  }
  if (series instanceof Term) {
    try {
      return `
      props: ${Term.propsSummary(series.props)},
      value: ${Term.summaryOf(series.value)}
      `;
    } catch (error) {
      console.log('error in summaryOf for ', series, error);
      return `err: ${error.message}`;
    }
  }
  return util.inspect(series);
};

Term.typeOf = (item) => {
  if (item === null) return TYPE_NULL;
  if (typeof item === 'string') return TYPE_STRING;
  if (Array.isArray(item)) return TYPE_ARRAY;
  if (item instanceof Term) return TYPE_TERM;
  if (typeof item === 'object') {
    return TYPE_OBJECT;
  }
  return TYPE_VALUE;
};
